import { useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import beTwMerge from '@/utils/beTwMerge';
import { GaEventsProps } from '@/store/types/IBuilder';
import useStore from '@/store/useStore';
import { getAnchorId } from '@/utils/builderUtils';
import HeaderBanner from './HeaderBanner';

export interface TopPurpleBar {
  text: string;
  mobileText?: string;
  link?: string;
  category?: string[];
  displayPopup?: boolean;
}

export type RotatingPromotionBannerProps = {
  backgroundColor: string;
  fontColor: string;
  anchorLinkID?: string;
  gaEvents?: GaEventsProps[];
  content?: any;
  messages?: TopPurpleBar[];
  PromoMessagePosition?: string;
};

export default function RotatingPromotionBanner({
  backgroundColor = '#183E40',
  fontColor = '#FFFFFF',
  gaEvents,
  anchorLinkID,
  content,
  messages,
  PromoMessagePosition = '1',
}: RotatingPromotionBannerProps) {
  const contentFromStore = useStore(state => state.builderData?.promo?.topPurpleBar);

  const promoContent = content || contentFromStore;
  function setHeaderBanners() {
    const slides: any = [];

    // Add incoming messages in their current order
    if (messages) {
      messages.forEach((slide, index) => {
        const messageSlide = {
          content: {
            text: slide.text,
            mobileText: slide.mobileText,
            link: slide.link,
          },
          backgroundColor,
          fontColor,
          gaEvents,
          key: `message-${index}`,
        };

        slides.push(messageSlide);
      });
    }

    // Push promoBanner into its specified position if it exists
    if (promoContent && PromoMessagePosition) {
      const position = parseInt(PromoMessagePosition, 10) - 1;
      const messageSlide = {
        content: promoContent,
        backgroundColor,
        fontColor,
        gaEvents,
        key: 'promoBanner',
      };
      slides.splice(position, 0, messageSlide);
    }

    // Create HeaderBanner components from slides array
    return slides.map((message: any, index: number) => (
      <HeaderBanner
        key={message.key || index}
        backgroundColor={message.backgroundColor}
        fontColor={message.fontColor}
        content={message.content}
        gaEvents={message.gaEvents}
      />
    ));
  }

  const headerBanners = setHeaderBanners();

  return (
    <div
      {...getAnchorId(anchorLinkID)}
      className={beTwMerge(
        'outer tw-no-underline lg:tw-h-[32px] tw-font-base tw-overflow-hidden max-[1023px]:tw-pl-[16px] max-[1023px]:tw-pr-[16px] max-[767px]:tw-text-[10px] tw-text-[12px] tw-tracking-widest tw-min-h-[32px] tw-relative tw-pl-[32px] tw-pr-[32px]'
      )}
      style={{ backgroundColor }}
    >
      <Fader fadeIn={500} fadeOut={500} delay={5500} className="tw-flex-1" fontColor={fontColor}>
        {headerBanners}
      </Fader>
    </div>
  );
}

const fadeInTransition = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 100;
  }
`;

const fadeOutTransition = keyframes`
  from {
    opacity: 100;
  }
  to {
    opacity: 0;
  }
`;

const Container = styled.div.attrs<{ $fadeIn?: number; $fadeOut?: number }>(props => ({
  $fadeIn: props.$fadeIn || 300,
  $fadeOut: props.$fadeOut || 300,
}))`
  .fader-child {
    display: none;
    opacity: 0;
    animation: ${fadeInTransition} ${({ $fadeIn }) => $fadeIn}ms ease-in-out;
  }
  .fader-child.fading {
    opacity: 0;
    animation: ${fadeOutTransition} ${({ $fadeOut }) => $fadeOut}ms ease-in-out;
  }
  .fader-child.active {
    opacity: 1;
  }
  .fader-child.selected {
    display: block;
  }
`;

interface FaderProps {
  children: ReactNode[];
  fadeIn: number;
  fadeOut: number;
  delay: number;
  className?: string;
  fontColor?: string;
}

export function Fader({ children, fadeIn, fadeOut, delay, className, fontColor = 'white' }: FaderProps) {
  const [childIndex, setChildIndex] = useState<number>(0);
  const [visible, setVisible] = useState<boolean>(true);
  const [fading, setFading] = useState<boolean>(false);
  const timeoutRef1 = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef2 = useRef<NodeJS.Timeout | null>(null);

  const clearTimeouts = useCallback(() => {
    if (timeoutRef1.current) {
      clearTimeout(timeoutRef1.current);
    }
    if (timeoutRef2.current) {
      clearTimeout(timeoutRef2.current);
    }
  }, []);

  const handleNext = () => {
    clearTimeouts();
    setChildIndex(prevIndex => (prevIndex + 1) % children.length);
  };

  const handlePrev = () => {
    clearTimeouts();
    setChildIndex(prevIndex => (prevIndex - 1 + children.length) % children.length);
  };

  const triggerCycle = useCallback(() => {
    setVisible(true);
    timeoutRef1.current = setTimeout(() => {
      setVisible(false);
      setFading(true);
      timeoutRef2.current = setTimeout(() => {
        setFading(false);
        setChildIndex(prevIndex => (prevIndex + 1) % children.length);
      }, fadeOut);
    }, delay);
  }, [children.length, delay, fadeOut]);

  useEffect(() => {
    if (children.length > 1) {
      clearTimeouts();
      triggerCycle();
    }
    return () => {
      clearTimeouts();
    };
  }, [children.length, childIndex, triggerCycle, clearTimeouts]);

  return (
    <div
      className={beTwMerge(
        'inner tw-relative tw-flex tw-flex-nowrap tw-justify-between tw-w-full  tw-items-center tw-min-h-[30px] tw-gap-[10px] max-[1023px]:tw-h-[40px]',
        className
      )}
      data-ge-hide=""
    >
      {children?.length > 1 && (
        <span
          className="tw-self-center tw-leading-[0] tw-cursor-pointer tw-w-[14px] tw-text-left"
          onClick={handlePrev}
          role="button"
          aria-label="Previous promotion"
          tabIndex={0}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              handlePrev();
            }
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <g clipPath="url(#clip0_5_310)">
              <path d="M10 14L3 7L10 0" stroke={fontColor} />
            </g>
            <defs>
              <clipPath id="clip0_5_310">
                <rect width="14" height="14" fill={fontColor} />
              </clipPath>
            </defs>
          </svg>
        </span>
      )}
      <Container $fadeIn={fadeIn} $fadeOut={fadeOut} className="fader-container tw-relative tw-flex-1">
        {children.map((child, index) => (
          <div
            key={index}
            className={beTwMerge(
              'fader-child tw-w-full tw-top-[0] tw-left-[0]',
              index === childIndex && 'selected',
              visible && 'active',
              fading && 'fading'
            )}
          >
            {child}
          </div>
        ))}
      </Container>
      {children?.length > 1 && (
        <span
          className="tw-self-center tw-leading-[0] tw-cursor-pointer tw-w-[14px] tw-text-right"
          onClick={handleNext}
          role="button"
          aria-label="Next promotion"
          tabIndex={0}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              handleNext();
            }
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <g clipPath="url(#clip0_5_312)">
              <path d="M3 4.76837e-07L10 7L3 14" stroke={fontColor} />
            </g>
            <defs>
              <clipPath id="clip0_5_312">
                <rect width="14" height="14" fill={fontColor} />
              </clipPath>
            </defs>
          </svg>
        </span>
      )}
    </div>
  );
}
